import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';

import {
  SECTION_ABN,
  SECTION_BUDGET,
  SECTION_CATEGORIES,
  SECTION_CLASSIFICATION,
  SECTION_TRANSACTIONS,
  SECTION_CHART,
  SECTION_REVOLUT,
  SECTION_ICS,
  SECTION_SWIBECO,
  SECTION_CREDIT_AGRICOLE,
  SECTION_CREDIT_MUTUEL_EUR,
  SECTION_CREDIT_MUTUEL_CHF,
  SECTION_REPORTING,
  SECTION_BAF,
  SECTION_SETTINGS,
  SECTION_ABOUT,

  SECTION_HISTORY,
  SECTION_BUNQ,
  SECTION_ASN,
  SECTION_UBS,
} from '../../constants/sections';
import { ALL_BANKS } from '../../constants/banks';
import ASTIButton from '../buttons/ASTIButton';

function ASTINavbarButtons(props) {
  const { changeSection, section } = props;
  const [openImportMenu, setOpenImportMenu] = useState(false);
  const [accounts, setAccounts] = useState([]);

  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await fetch('/api/accounts');
        if (response.ok) {
          const data = await response.json();
          setAccounts(data);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);
  // Get unique bank IDs from accounts
  const bankIdToSection = {
    ABN: SECTION_ABN,
    REVOLUT: SECTION_REVOLUT,
    ICS: SECTION_ICS,
    CREDIT_AGRICOLE: SECTION_CREDIT_AGRICOLE,
    CREDIT_MUTUEL: SECTION_CREDIT_MUTUEL_EUR, // Use EUR section for Crédit Mutuel
    BUNQ: SECTION_BUNQ,
    UBS: SECTION_UBS,
    SWIBECO: SECTION_SWIBECO,
  };


  const bankLabels = {};
  ALL_BANKS.forEach((b) => {
    bankLabels[b.id] = b.label;
  });

  const importSelected =
    section === SECTION_ABN ||
    section === SECTION_REVOLUT ||
    section === SECTION_ICS ||
    section === SECTION_SWIBECO ||
    section === SECTION_CREDIT_AGRICOLE ||
    section === SECTION_CREDIT_MUTUEL_EUR ||
    section === SECTION_CREDIT_MUTUEL_CHF ||
    section === SECTION_BUNQ ||
    section === SECTION_ASN ||
    section === SECTION_UBS;

  const handleOpenImportMenu = async () => {
    // Refetch accounts when menu opens to show latest
    try {
      const response = await fetch('/api/accounts');
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
    setOpenImportMenu(true);
  };

  const handleCloseImportMenu = () => {
    setOpenImportMenu(false);
  };
  const handleSelectImportSection = (nextSection) => {
    changeSection(nextSection);
    setOpenImportMenu(false);
  };
  
  return (
    <>
      <ASTIButton
        text="Settings"
        onClick={() => changeSection(SECTION_SETTINGS)}
        selected={section === SECTION_SETTINGS}
      />
      <Box
        position="relative"
        onMouseEnter={handleOpenImportMenu}
        onMouseLeave={handleCloseImportMenu}
      >
        <Button
          onClick={() => setOpenImportMenu((prev) => !prev)}
          sx={{
            textTransform: 'none',
            textDecoration: importSelected ? 'underline' : 'none'
          }}
        >
          <Typography variant="button">Import</Typography>
        </Button>

        {openImportMenu && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            mt={0.5}
            minWidth={220}
            border="1px solid #D4B896"
            borderRadius="6px"
            bgcolor="#FBF5EC"
            zIndex={1300}
            boxShadow="0 8px 20px rgba(0,0,0,0.45)"
            p={0.5}
            maxHeight={400}
            overflow="auto"
          >
            {accounts.length === 0 ? (
              <Typography
                sx={{
                  color: '#7A5035',
                  fontFamily: 'Courier New',
                  fontSize: '0.8rem',
                  px: 2,
                  py: 1,
                }}
              >
                No accounts configured
              </Typography>
            ) : (
              accounts
                // Group accounts by bank_id, keeping only the first for display
                .reduce((acc, account) => {
                  if (!acc.find((a) => a.bank_id === account.bank_id)) {
                    acc.push(account);
                  }
                  return acc;
                }, [])
                .sort((a, b) => {
                  const aLabel = bankLabels[a.bank_id] || a.bank_id;
                  const bLabel = bankLabels[b.bank_id] || b.bank_id;
                  return aLabel.localeCompare(bLabel);
                })
                .map((account) => {
                const bankLabel = bankLabels[account.bank_id] || account.bank_id;
                let accountSection = bankIdToSection[account.bank_id] || SECTION_ABN;
                // For Crédit Mutuel, choose section based on currency
                if (account.bank_id === 'CREDIT_MUTUEL') {
                  accountSection = account.currency === 'CHF' ? SECTION_CREDIT_MUTUEL_CHF : SECTION_CREDIT_MUTUEL_EUR;
                }

                return (
                  <Button
                    key={`${account.bank_id}`}
                    fullWidth
                    onClick={() => handleSelectImportSection(accountSection)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      color: '#2B1810',
                      fontSize: '0.9rem',
                      textDecoration: accountSection === section ? 'underline' : 'none',
                      px: 2,
                      py: 0.75,
                      '&:hover': { backgroundColor: '#F0E3D0' },
                    }}
                  >
                    {bankLabel}
                  </Button>
                );
              })
            )}
          </Box>
        )}
      </Box>


      <ASTIButton
        text="Categories"
        onClick={() => changeSection(SECTION_CATEGORIES)}
        selected={section === SECTION_CATEGORIES}
      />

      <ASTIButton
        text="Auto Categorization"
        onClick={() => changeSection(SECTION_CLASSIFICATION)}
        selected={section === SECTION_CLASSIFICATION}
      />
      <ASTIButton
        text="Transactions"
        onClick={() => changeSection(SECTION_TRANSACTIONS)}
        selected={section === SECTION_TRANSACTIONS}
      />
      <ASTIButton
        text="Spending Profile"
        onClick={() => changeSection(SECTION_CHART)}
        selected={section === SECTION_CHART}
      />
      <ASTIButton
        text="Budget"
        onClick={() => changeSection(SECTION_BUDGET)}
        selected={section === SECTION_BUDGET}
      />
      <ASTIButton
        text="B/A/F Matrix"
        onClick={() => changeSection(SECTION_BAF)}
        selected={section === SECTION_BAF}
      />
      <ASTIButton
        text="Reporting"
        onClick={() => changeSection(SECTION_REPORTING)}
        selected={section === SECTION_REPORTING}
      />
      <ASTIButton

        text="About"
        onClick={() => changeSection(SECTION_ABOUT)}
        selected={section === SECTION_ABOUT}
      />
      <ASTIButton
        text="History"
        onClick={() => changeSection(SECTION_HISTORY)}
        selected={section === SECTION_HISTORY}
      />
    </>
  );
}

export default ASTINavbarButtons;

ASTINavbarButtons.propTypes = {
  section: PropTypes.string.isRequired,
  changeSection: PropTypes.func.isRequired,
};
